import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';

import { END_POINTS } from '../../../services';
import { getSpecificWeekDate } from '../../../utils/date';
import useGraphApiData from '../useGraphApiData';
import {
  GRAPH_GENERATE_STATUS,
  SHIFT_MANAGE_STATUS,
  facilityActions,
  useFacilityContext,
} from './FacilityProvider';
import { FacilitySnapShot } from './FacilityProvider.types';
import {
  ShiftProviderAction,
  ShiftProviderContextProps,
  ShiftProviderProps,
  ShiftProviderState,
  ShiftProviderTypes,
} from './ShiftProvider.types';

const initialState: ShiftProviderState = {
  shifts: { data: {}, isLoading: false },
};
const shiftContextDefaultValue: ShiftProviderContextProps = {
  state: initialState,
  dispatch: () => {},
};
const ShiftProviderContext = createContext<ShiftProviderContextProps>(
  shiftContextDefaultValue
);
export const useShifts = () => useContext(ShiftProviderContext);
export default function ShiftProvider({
  children,
  scenario,
}: ShiftProviderProps) {
  const [state, dispatch] = useReducer(shiftProviderReducer, initialState);
  return (
    <ShiftProviderContext.Provider value={{ state, dispatch }}>
      {scenario && <ShiftDetailFetcher scenario={scenario} />}
      {children}
    </ShiftProviderContext.Provider>
  );
}
const ShiftDetailFetcher = ({ scenario }: { scenario: string }) => {
  const {
    state: { facilitySnapshots, selectedFacility },
    dispatch: facilityDispatch,
  } = useFacilityContext();
  const activeFacilitySnapShot: FacilitySnapShot = {
    userGraphGenerationStatus: GRAPH_GENERATE_STATUS.IDLE_GENERATED_GRAPH,
    userShiftManageStatus: SHIFT_MANAGE_STATUS.IDLE,
    clientId: undefined,
    ...(facilitySnapshots?.[selectedFacility] || {}),
  };
  const { dispatch } = useShifts();
  const [isRegenerated, setIsRegenerated] = useState(false);
  const [shiftSessionId, setPrevShiftSessionId] = useState(
    activeFacilitySnapShot.clientId
  );
  if (shiftSessionId !== activeFacilitySnapShot.shiftSessionId) {
    setPrevShiftSessionId(activeFacilitySnapShot.shiftSessionId);
    setIsRegenerated(true);
  }
  // const [prevShiftData, setPrevShiftData] =
  //   useState<ShiftProviderState['shifts']>();
  const {
    data: shiftDataRes,
    isLoading: isShiftDataLoading,
    reValidate,
  } = useGraphApiData(END_POINTS.DASHBOARD.getAllShifts(scenario), {
    params: {
      user_id: activeFacilitySnapShot?.shiftSessionId,
    },
  });
  useEffect(() => {
    if (
      activeFacilitySnapShot.userShiftManageStatus ===
        SHIFT_MANAGE_STATUS.SUCCESS ||
      activeFacilitySnapShot.userShiftManageStatus ===
        SHIFT_MANAGE_STATUS.DEFAULT
    ) {
      if (!isRegenerated) reValidate();

      setIsRegenerated(false);
      facilityDispatch(
        facilityActions.updateShiftSessionId(
          selectedFacility,
          SHIFT_MANAGE_STATUS.IDLE
        )
      );
    }
  }, [
    activeFacilitySnapShot.userShiftManageStatus,
    facilityDispatch,
    isRegenerated,
    reValidate,
    selectedFacility,
  ]);
  const shiftData: ShiftProviderState['shifts'] = useMemo(() => {
    const newShiftData: ShiftProviderState['shifts'] = {
      isLoading: isShiftDataLoading,
      data: {},
    };
    if (Array.isArray(shiftDataRes) && shiftDataRes.length > 0) {
      shiftDataRes
        .sort((a, b) => {
          if (a.provider_type_name === b.provider_type_name) {
            return (
              new Date(a?.shift_start).valueOf() -
              new Date(b?.shift_start).valueOf()
            );
          } else if (a.provider_type_name?.toLowerCase() === 'physician')
            return -1;
          else if (a.provider_type_name?.toLowerCase() === 'app') return 1;
          return 0;
        })
        .forEach((d: any, index) => {
          let endDate = new Date(d?.shift_end);
          const startDate = new Date(d?.shift_start);
          let newEndDate = getSpecificWeekDate(
            startDate.toISOString(),
            endDate.getDay(),
            endDate.getHours()
          );
          newShiftData.data = {
            ...(newShiftData?.data || {}),
            [d?.shift_id || '']: {
              id: d?.shift_id || '',
              startDate: startDate.toISOString(),
              endDate: newEndDate,
              shiftLength: d?.shift_length || 0,
              providerType: d?.provider_type_name || '',
              providerCount: d?.provider_count || 0,
              hours: d?.shift_length || 0,
            },
          };
        });
    }
    return newShiftData;
  }, [shiftDataRes, isShiftDataLoading]);
  // if (JSON.stringify(prevShiftData) !== JSON.stringify(shiftData)) {
  //   setPrevShiftData(shiftData);
  //   onLoadData?.(shiftData);
  // }
  useEffect(() => {
    // onLoadData?.(shiftData);
    dispatch(shiftActions.setShiftLoading(shiftData.isLoading));
    dispatch(shiftActions.setShifts(shiftData.data));
  }, [shiftData, dispatch]);
  return null;
};
export const shiftActions = {
  setShiftLoading: (isLoading: boolean = false) => {
    return {
      type: ShiftProviderTypes.SET_SHIFT_LOADING,
      payload: isLoading,
    };
  },
  setShifts: (v: ShiftProviderState['shifts']['data']) => {
    return {
      type: ShiftProviderTypes.SET_SHIFT,
      payload: v,
    };
  },
  updateShifts: (v: ShiftProviderState['shifts']['data']) => {
    return {
      type: ShiftProviderTypes.UPDATE_SHIFT,
      payload: v,
    };
  },
};
function shiftProviderReducer(
  state: ShiftProviderState,
  action: ShiftProviderAction
): ShiftProviderState {
  const { payload, type } = action;
  switch (type) {
    case ShiftProviderTypes.SET_SHIFT: {
      return {
        ...state,
        shifts: { ...(state.shifts || {}), data: { ...(payload || {}) } },
      };
    }
    case ShiftProviderTypes.SET_SHIFT_LOADING: {
      return {
        ...state,
        shifts: { ...(state?.shifts || {}), isLoading: payload },
      };
    }

    case ShiftProviderTypes.UPDATE_SHIFT: {
      return {
        ...state,
        shifts: {
          ...state.shifts,
          data: { ...(state?.shifts?.data || {}), ...(payload || {}) },
        },
      };
    }
    default: {
      throw Error('Unknown action: ' + action.type);
    }
  }
}
